import { AssignmentTypeEnum } from 'api/model';
import { KeyListRow } from 'components/KeyValueList';

export type ExcludableKeyListRow = KeyListRow & {
  exclude?: () => boolean;
  field?: AssignmentField;
};

export type FixedPosition = {
  latitude: number;
  longitude: number;
  address: string;
  assignmentIds: number[];
  enterAndExitData?: {
    assignmentId: number;
    enterTimestamp?: Date;
    exitTimestamp?: Date;
  }[];
  name: string;
};

export enum AssignmentField {
  assignmentDeviationID,
  assignmentCancelTypeID,
  assignmentTypeID,
  assignmentStatusID,
  sendCustomerUpdateMessages,

  fromName,
  fromAddress,
  fromCity,
  fromZip,
  fromCounty,

  toName,
  toAddress,
  toCity,
  toZip,
  toCounty,

  bookedTo,
  startTime,
  endTime,
  fixedAssignmentTimeInHours,
  breakTime,
  extraInfo,

  assignedTo,
  assignedToRating,
  assignedToRatingReason,

  estimatedDuration,
  estimatedStartTime,
  estimatedLoadingTime,
  estimatedUnloadingTime,
  systemEstimatedDurationExact,
  systemEstimatedDurationApprox,

  expectedExpenses,

  standardizedComments,

  allowPrivateStopOver,

  keyHandedIn,

  shouldGoOnTruck,

  hasMultiPurchaseAssignmentSiblingsWithEstimatedDurationAndStartTime,

  standByAssignmentTimeInHours,
  standByAssignmentIsEligible,
  standByAssignmentTimeReason,

  assigneeShouldBookTheirOwnTicket,
  ticketInfo,
  firstPossibleStartTime, // not part of model, but used to validate estimated start time
  lastPossibleEndTime, // not part of model, but used to validate estimated end time
  isNewAssignment,
  fromAddressIsOutSideHomeArea, // not part of model, but used to validate estimated end time
  toAddressIsOutSideHomeArea, // not part of model, but used to validate estimated end time
  isUsersFirstAssignmentOfDay,
  isUsersLastAssignmentOfDay,
  isUserBetweenAssignments,
  stayOverTypeID,
  stayOverInfo,

  // in-edit mode planned stopover
  addingStopOver,
  stopOverName,
  stopOverAddress,
  stopOverCity,
  stopOverZip,
  stopOverCounty,
  stopOverEstimatedDuration,
  stopOverFixedAssignmentTimeInHours,

  // in-edit mode personal transportation to assignment
  addingPersonalTransportationToAssignment,
  personalTransportationToAssignmentEstimatedStartTime,
  personalTransportationToAssignmentEstimatedDuration,
  personalTransportationToAssignmentFixedAssignmentTimeInHours,
  personalTransportationToAssignmentName,
  personalTransportationToAssignmentAddress,
  personalTransportationToAssignmentCity,
  personalTransportationToAssignmentZip,
  personalTransportationToAssignmentCounty,
  personalTransportationToAssignmentAssignmentTypeID,
  personalTransportationToAssigneeShouldBookTheirOwnTicket,
  personalTransportationToTicketInfo,
  personalTransportationToExpectedExpenses,

  // in-edit mode personal transportation from assignment
  addingPersonalTransportationFromAssignment,
  personalTransportationFromAssignmentEstimatedDuration,
  personalTransportationFromAssignmentEstimatedStartTime,
  personalTransportationFromAssignmentFixedAssignmentTimeInHours,
  personalTransportationFromAssignmentName,
  personalTransportationFromAssignmentAddress,
  personalTransportationFromAssignmentCity,
  personalTransportationFromAssignmentZip,
  personalTransportationFromAssignmentCounty,
  personalTransportationFromAssignmentAssignmentTypeID,
  personalTransportationFromAssigneeShouldBookTheirOwnTicket,
  personalTransportationFromTicketInfo,
  personalTransportationFromExpectedExpenses,

  // Samåkning: bäraruppdraget (id, '' eller 'none' = ingen bärare i systemet) och motiveringen
  // när bärare saknas. ridesAlongRequired är inte en del av modellen — den säger om kravet
  // gäller (ny samåkning) så att validatorn kan läsa det ur formulärets state.
  ridesAlongWithAssignmentID,
  ridesAlongExemptionReason,
  ridesAlongRequired,

  // Nödfallsmotiveringen när en runner väljs till ett uppdrag med kundkontakt (FT-väljaren).
  // Enbart indata till servern, som loggar den som intern kommentar på ärendet.
  runnerCustomerLegOverrideReason,
  // Laddbuffert för elbil (ms) som TL valt i dropdownen under den här redigeringen.
  // '' = inget val — det sparade värdet (eller regeln, om dess indata ändrats) gäller.
  manualElectricCarBufferTime,
  // Varför Bokad till flyttas i den här redigeringen (BookedToChangeReasonEnum som sträng, ''
  // = inte vald) och en valfri kommentar. originalBookedTo är den sparade tiden vid inläsning —
  // inte en del av modellen, men det enda sättet för validatorn att se att tiden ändrats.
  bookedToChangeReason,
  bookedToChangeComment,
  originalBookedTo,
}

/**
 * Om Bokad till har flyttats på ett befintligt uppdrag under redigeringen. Jämförs på minuten,
 * som datetime-fältet skriver den — samma upplösning som backend rundar till.
 */
export const isBookedToChanged = (form: {
  [key in AssignmentField]?: string;
}) =>
  !!form[AssignmentField.originalBookedTo] &&
  !!form[AssignmentField.bookedTo] &&
  form[AssignmentField.bookedTo] !== form[AssignmentField.originalBookedTo];

export const isSelfTransportationAssignment = (
  assignmentType: AssignmentTypeEnum
) => {
  switch (assignmentType) {
    case AssignmentTypeEnum.GoByBoat:
    case AssignmentTypeEnum.GoByPlane:
    case AssignmentTypeEnum.GoByTrain:
    case AssignmentTypeEnum.GoByBus:
    case AssignmentTypeEnum.GoByTaxi:
    case AssignmentTypeEnum.GoByPublicTransport:
      return true;

    default:
      return false;
  }
};
